import { useState, useEffect, useRef } from 'react';
import ContactPanel from './ContactPanel';
import VodacomIcon from './VodacomIcon';
import { usePageTransition } from '../context/TransitionContext';

const STATIONS = [
  {
    range: [0, 0.17],
    label: 'VODACOM',
    headline: <>THE FUTURE<br />IS EXCITING.<br /><span>READY?</span></>,
    sub: 'Powering a digital society that leaves no one behind.',
    variant: 'hero',
    buttons: [
      { label: 'Explore Products', primary: true },
      { label: 'Why Vodacom', primary: false },
    ],
  },
  {
    range: [0.15, 0.32],
    label: 'THE DEVICE · 02',
    headline: <>THIS<br /><span>ISN'T JUST</span><br />A PHONE.</>,
    sub: 'A device engineered at the edge of possibility. Forged in charcoal. Powered in red.',
    variant: 'default',
    buttons: [
      { label: 'Shop Now', primary: true, path: '/phones' },
      { label: 'Learn More', primary: false },
    ],
  },
  {
    range: [0.30, 0.48],
    label: 'PERFORMANCE · 03',
    headline: <>POWERED<br /><span>BY SPEED.</span></>,
    sub: 'Next-gen silicon. Real-time AI processing. Zero latency between thought and action.',
    variant: 'default',
    buttons: [
      { label: 'View Data Plans', primary: true, path: '/data-deals' },
      { label: 'Learn More', primary: false },
    ],
  },
  {
    range: [0.46, 0.63],
    label: 'WEARABLES · 04',
    headline: <>SO PORTABLE,<br /><span>IT'S WEARABLE.</span></>,
    sub: 'Seamlessly paired with your smartwatch. One ecosystem. Total control from your wrist.',
    variant: 'default',
    buttons: [
      { label: 'Shop Wearables', primary: true, path: '/wearables' },
      { label: 'Learn More', primary: false },
    ],
  },
  {
    range: [0.61, 0.79],
    label: 'INFRASTRUCTURE · 05',
    headline: <>RHYTHM<br />OF SPEED.</>,
    sub: 'Our fibre backbone spans the nation. Always-on. Always fast. Always red.',
    variant: 'banner',
    buttons: [
      { label: 'Get Fibre', primary: true, path: '/fibre' },
      { label: 'Our Network', primary: false },
    ],
  },
  {
    range: [0.77, 1.01],
    label: 'BUSINESS · 06',
    headline: <>BUILT FOR<br /><span>BUSINESS.</span></>,
    sub: 'Enterprise-grade connectivity. Scalable solutions designed for growth. Your ambition, our infrastructure.',
    variant: 'default',
    buttons: [
      { label: 'Business Solutions', primary: true, path: '/business' },
      { label: 'Contact Sales', primary: false },
    ],
  },
];

const FADE_OUT_MS = 280;

export default function HUDOverlay({ scrollProgress }) {
  const navigateTo = usePageTransition();
  const [shownIdx, setShownIdx]   = useState(0);
  const [phase, setPhase]         = useState('in');
  const [dotActive, setDotActive] = useState(0);

  const pendingIdx = useRef(0);
  const timerRef   = useRef(null);

  useEffect(() => {
    const idx = STATIONS.findIndex(
      ({ range }) => scrollProgress >= range[0] && scrollProgress < range[1]
    );
    if (idx === -1) return;

    setDotActive(idx);

    if (idx === pendingIdx.current) return;
    pendingIdx.current = idx;

    if (timerRef.current) clearTimeout(timerRef.current);

    setPhase('out');

    timerRef.current = setTimeout(() => {
      setShownIdx(idx);
      setPhase('in');
    }, FADE_OUT_MS);
  }, [scrollProgress]);

  const s      = STATIONS[shownIdx];
  const isHero = s.variant === 'hero';

  const stationText = (
    <div className={`station-text phase-${phase}${isHero ? ' station-text--hero' : ''}`}>
      <span className="section-label">{s.label}</span>
      <h1 className="headline">{s.headline}</h1>
      <p className="body-copy">{s.sub}</p>
      <div className="cta-buttons">
        {s.buttons.map((btn, i) => (
          <button
            key={i}
            className={`cta-btn cta-btn-${btn.primary ? 'primary' : 'secondary'}`}
            onClick={() => btn.path && navigateTo(btn.path)}
          >
            {btn.label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <div className={`hud-overlay${isHero ? ' station-hero' : ''}`}>
        {isHero ? (
          <>
            <div className="hero-text-col">{stationText}</div>
            <div className="hero-icon-col">
              <VodacomIcon scrollProgress={scrollProgress} />
            </div>
          </>
        ) : (
          stationText
        )}
      </div>

      <nav className="progress-dots" aria-label="Sections">
        {STATIONS.map((_, i) => (
          <div key={i} className={`dot${i === dotActive ? ' active' : ''}`}>
            {String(i + 1).padStart(2, '0')}
          </div>
        ))}
      </nav>

      <ContactPanel visible={scrollProgress > 0.80} />
    </>
  );
}
